'use strict';

'use strict';

import React, {
    useCallback,
    useMemo
} from 'react';
import {
    useDispatch,
    useSelector
} from 'react-redux';
import _ from 'lodash';
import {keys} from 'ts-transformer-keys';

import {__} from '~/shared/src/utilities/messageCatalog';
import {
    ColumnConfig,
    ColumnDefinition,
    ColumnSorter
} from '~/shared/src/validationSchemes/modules/overview';
import {buildDataDescriptor} from '~/shared/src/utilities/dataStructure';
import {getDataStructureForSqlQuery} from '~/shared/src/utilities/modules/queryLoader';

import DataTable,
{
    AllProps as DataTableAllProps,
    componentProps as dataTableComponentProps,
    ComponentProps as DataTableComponentProps,
    defaultProps as dataTableDefaultProps,
    DefaultProps as DataTableDefaultProps,
    Props as DataTableProps
} from '~/frontend/src/components/base/DataTable';
import Toolbar, {
    ComponentProps as ToolbarComponentProps,
    componentProps as toolbarComponentProps
} from '~/frontend/src/components/base/ItemFinder/history/toolbar';

import {getColumnConfigFromColumnDefinition} from '~/frontend/src/components/base/DataTable/utilities';
import {extractViewPropsFromProps} from '~/frontend/src/components/base/ItemFinder/overview/utilities';

import {
    AppDispatch,
    StoreState
} from '~/frontend/src/store';
import loadDataFromBE from '~/frontend/src/actions/data/loadData';
import {PanelElusiveDataState} from '~/frontend/src/reducers/elusiveData';


export type DefaultProps = DataTableDefaultProps & {
    dataContext? : string;
};
export const defaultProps : Required<DefaultProps> = {
    ...dataTableDefaultProps,
    dataContext: 'itemFinderReferencesHistory'
};

export type Props = Omit<DataTableProps, 'dataContext' | 'reload'> & {
    schema : Schema;
    rootItemId : number;
};

export type ComponentProps = DefaultProps & Props;
export const componentProps = keys<ComponentProps>();

type AllProps = Required<DefaultProps> & Props;


type ElusiveData = PanelElusiveDataState;


const ReferencesHistory = ( props : AllProps ) => {
    const dispatch : AppDispatch   = useDispatch(),
          {
              uid,
              rootItemId,
              schema,
              dataContext
          }                        = props,
          user                     = useSelector( ( state : StoreState ) => state.session?.user ? state.session.user : undefined ),
          elusiveData              = useSelector( ( state : StoreState ) => state.elusiveData?.[ uid ] as ElusiveData | undefined ),
          xhr                      = useSelector( ( state : StoreState ) => state.xhr[ uid ] ),
          messageCatalogTimestamp  = useSelector( ( state : StoreState ) => state.messagecatalog?.timestamp ? state.messagecatalog.timestamp : 0 ),
          language                 = useMemo(
              () => {
                  return user?.language ?? '';
              },
              [
                  user
              ]
          ),
          isLoading                = useMemo(
              () => {
                  return !!xhr?.[ 'loadReferencesHistoryData' ];
              },
              [
                  xhr
              ]
          ),
          dataStructure            = useMemo(
              () => {
                  return getDataStructureForSqlQuery();
              },
              [
                  'onlyOnce'
              ]
          ),
          defaultColumnDefinitions = useMemo(
              () => {
                  return [
                      {
                          key           : 'id',
                          field         : 'id',
                          header        : __( 'YN.base.common.attribute.id' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'id' }, dataStructure ),
                              valueType: 'INTEGER'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 100
                      },
                      {
                          key           : 'referenceSetId',
                          field         : 'referenceSetId',
                          header        : __( 'YN.base.common.attribute.referenceSetId' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'referenceSetId' }, dataStructure ),
                              valueType: 'INTEGER'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 100
                      },
                      {
                          key           : 'srcItemId',
                          field         : 'srcItemId',
                          header        : __( 'YN.base.common.attribute.srcItemId' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'srcItemId' }, dataStructure ),
                              valueType: 'INTEGER'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 100
                      },
                      {
                          key           : 'srcItemTypeName',
                          field         : 'srcItemTypeName',
                          header        : __( 'YN.base.common.attribute.srcItemTypeName' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'srcItemTypeName' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      },
                      {
                          key           : 'srcItemKey',
                          field         : 'srcItemKey',
                          header        : __( 'YN.base.common.attribute.srcItemKey' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'srcItemKey' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      },
                      {
                          key           : 'srcItemName',
                          field         : 'srcItemName',
                          header        : __( 'YN.base.common.attribute.srcItemName' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'srcItemName' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      },
                      {
                          key           : 'referenceTypeName',
                          field         : 'referenceTypeName',
                          header        : __( 'YN.base.common.attribute.referenceTypeName' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'referenceTypeName' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      },
                      {
                          key           : 'destItemId',
                          field         : 'destItemId',
                          header        : __( 'YN.base.common.attribute.destItemId' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'destItemId' }, dataStructure ),
                              valueType: 'INTEGER'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 100
                      },
                      {
                          key           : 'destItemTypeName',
                          field         : 'destItemTypeName',
                          header        : __( 'YN.base.common.attribute.destItemTypeName' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'destItemTypeName' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      },
                      {
                          key           : 'destItemKey',
                          field         : 'destItemKey',
                          header        : __( 'YN.base.common.attribute.destItemKey' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'destItemKey' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      },
                      {
                          key           : 'destItemName',
                          field         : 'destItemName',
                          header        : __( 'YN.base.common.attribute.destItemName' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'destItemName' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      },
                      {
                          key           : 'statusId',
                          field         : 'statusId',
                          header        : __( 'YN.base.common.attribute.statusId' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'statusId' }, dataStructure ),
                              valueType: 'STATUSID'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 100
                      },
                      {
                          key           : 'modifierUserId',
                          field         : 'modifierUserId',
                          header        : __( 'YN.base.common.attribute.modifierUserId' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'modifierUserId' }, dataStructure ),
                              valueType: 'STRING'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 150
                      },
                      {
                          key           : 'validFromTime',
                          field         : 'validFromTime',
                          header        : __( 'YN.base.common.attribute.validFromTime' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'validFromTime' }, dataStructure ),
                              valueType: 'TIMESTAMP'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      },
                      {
                          key           : 'validToTime',
                          field         : 'validToTime',
                          header        : __( 'YN.base.common.attribute.validToTime' ),
                          dataDescriptor: {
                              ...buildDataDescriptor( { queryFieldName: 'validToTime' }, dataStructure ),
                              valueType: 'TIMESTAMP'
                          },
                          filter        : true,
                          editor        : false,
                          width         : 200
                      }
                  ] as ColumnDefinition[];
              },
              [
                  dataStructure,
                  language,
                  messageCatalogTimestamp
              ]
          ),
          defaultColumns           = useMemo(
              () => {
                  return defaultColumnDefinitions.map( iCD => getColumnConfigFromColumnDefinition( iCD ) ) as ColumnConfig[];
              },
              [
                  defaultColumnDefinitions
              ]
          ),
          defaultSorters           = useMemo(
              () => {
                  return [
                      { field: 'validFromTime', direction: 'DESC' },
                      { field: 'srcItemTypeName', direction: 'ASC' },
                      { field: 'referenceTypeName', direction: 'ASC' },
                      { field: 'destItemTypeName', direction: 'ASC' }
                  ] as ColumnSorter[];
              },
              [
                  'onlyOnce'
              ]
          ),
          loadData                 = useCallback(
              ( dataTableProps : DataTableAllProps ) => {
                  const params = {
                            schema: schema,
                            itemId: rootItemId,

                            filters: dataTableProps.filters,
                            sorters: dataTableProps.sorters,

                            start: ( dataTableProps.page - 1 ) * dataTableProps.rows,
                            limit: dataTableProps.rows
                        },
                        doLoad = async () => {
                            try {
                                await dispatch( loadDataFromBE(
                                    'finder/loadReferencesHistoryData',
                                    dataContext,
                                    uid,
                                    'loadReferencesHistoryData',
                                    schema,
                                    {
                                        params,
                                        extraMetaDataKeys: [ 'rowData', 'rowDataMapping', 'total' ]
                                    }
                                ) );
                            }
                            catch ( e ) {
                                console.error( e );
                            }
                        };

                  void doLoad();

                  return true;
              },
              [
                  rootItemId,
                  schema,
                  uid
              ]
          );

    return <>
        <Toolbar
            {..._.pick( props, toolbarComponentProps ) as ToolbarComponentProps}

            key={`${uid}_toolbar`}

            dataStructure={dataStructure}
            columnDefinitions={defaultColumnDefinitions}
            columns={Object.keys( props.columns ).length ? props.columns : defaultColumns}
            sorters={Object.keys( props.sorters ).length ? props.sorters : defaultSorters}

            exportLoadDataUrl="finder/loadReferencesHistoryData"
            viewSelectorPanelProps={{ ...extractViewPropsFromProps( props as unknown as DataTableAllProps ) }}

            className="yn-panel-toolbar-border-none-bottom"
        />
        <div style={{ position: 'relative' }} className="yn-flex-layout-parent">
            <DataTable
                {..._.pick( props, dataTableComponentProps ) as DataTableComponentProps}

                key={uid}

                dataStructure={dataStructure}
                columnDefinitions={defaultColumnDefinitions}
                columns={Object.keys( props.columns ).length ? props.columns : defaultColumns}
                sorters={Object.keys( props.sorters ).length ? props.sorters : defaultSorters}

                search={true}
                loading={isLoading}
                reload={loadData}

                value={elusiveData?.metaData?.rowData}
                totalRecords={elusiveData?.metaData?.total}
                valueFieldMapping={elusiveData?.metaData?.rowDataMapping}

                className="yn-flex yn-finder yn-datatable-border-none-top"
            />
        </div>
    </>;
};
ReferencesHistory.defaultProps = defaultProps;
ReferencesHistory.displayName = 'FinderHistory-References';

export default ReferencesHistory;
